import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  Grid,
  Paper,
  Avatar,
  useMediaQuery,
  useTheme,
  alpha
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  MarkEmailRead,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  FilterList,
  VolumeUp,
  VolumeOff,
  Business,
  Assignment,
  Schedule,
  Phone,
  Event,
  CheckCircle,
  Refresh,
  Dashboard,
  Clear,
  Description,
  Cancel,
  Group,
  Comment
} from '@mui/icons-material';
import { url } from "../constants/url";
import { useNavigate } from 'react-router-dom';
import soundGenerator from '../utils/soundGenerator';
import { useWebSocketContext } from '../context/WebSocketContext';
import { useThemeToggle } from '../context/ThemeContext';
import { useNotification } from '../context/NotificationContext';

const NotificationScreen = ({ userData }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode } = useThemeToggle();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const UID = userData?._id;
  const { isConnected } = useWebSocketContext(); 
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterAnchor, setFilterAnchor] = useState(null);

  const { refreshUnreadCount } = useNotification();

  const [filterType, setFilterType] = useState('all');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [audioSupported, setAudioSupported] = useState(true);

  // Theme colors from context
  const primaryColor = theme.palette.primary.main;
  const primaryLight = alpha(primaryColor, 0.15);
  const primaryLighter = alpha(primaryColor, 0.08);

  // Safe color helper function
  const getSafeColor = (colorConfig, notificationType) => {
    if (!colorConfig || !colorConfig.color) {
      console.warn(`Missing color configuration for notification type: ${notificationType}`);
      return theme.palette.primary.main; // fallback color
    }
    
    // Ensure the color is a valid string
    const colorValue = colorConfig.color;
    if (typeof colorValue !== 'string') {
      console.warn(`Invalid color value for notification type: ${notificationType}`, colorValue);
      return theme.palette.primary.main; // fallback color
    }
    
    return colorValue;
  };

  // Notification configuration with fallbacks
  const getNotificationConfig = (type) => {
    const config = notificationTypes[type];
    if (!config) {
      console.warn(`Unknown notification type: ${type}, using fallback`);
      return {
        icon: <NotificationsIcon sx={{ fontSize: 20 }} />,
        color: theme.palette.primary.main,
        label: 'Notification'
      };
    }
    return config;
  };

  const notificationTypes = {
    company_approval: { 
      icon: <Business sx={{ fontSize: 20 }} />, 
      color: theme.palette.success.main,
      label: 'Company'
    },
    team_invite: { 
      icon: <Assignment sx={{ fontSize: 20 }} />, 
      color: theme.palette.info.main,
      label: 'Team'
    },
    lead_assignment: { 
      icon: <Event sx={{ fontSize: 20 }} />, 
      color: primaryColor,
      label: 'Lead'
    },
    daily_followup: { 
      icon: <Schedule sx={{ fontSize: 20 }} />, 
      color: theme.palette.secondary.main,
      label: 'Follow-up'
    },
    meeting_reminder: { 
      icon: <Event sx={{ fontSize: 20 }} />, 
      color: '#E91E63',
      label: 'Meeting'
    },
    cnr_retry: { 
      icon: <Phone sx={{ fontSize: 20 }} />, 
      color: theme.palette.error.main,
      label: 'CNR'
    },
    status_change: { 
      icon: <Refresh sx={{ fontSize: 20 }} />, 
      color: theme.palette.grey[600],
      label: 'Status'
    },
    // QUOTATION-RELATED TYPES
    quotation_shared: {
      icon: <Description sx={{ fontSize: 20 }} />,
      color: theme.palette.warning.main,
      label: 'Quotation'
    },
    quotation_approved: {
      icon: <CheckCircle sx={{ fontSize: 20 }} />,
      color: theme.palette.success.main,
      label: 'Quotation'
    },
    quotation_rejected: {
      icon: <Cancel sx={{ fontSize: 20 }} />,
      color: theme.palette.error.main,
      label: 'Quotation'
    },
    quotation_created: {
      icon: <Description sx={{ fontSize: 20 }} />,
      color: theme.palette.info.main,
      label: 'Quotation'
    },
    team_collaboration_added: { 
    icon: <Group sx={{ fontSize: 20 }} />, 
    color: theme.palette.info.main,
    label: 'Team Collaboration'
  },
  team_collaboration_comment: { 
    icon: <Comment sx={{ fontSize: 20 }} />, 
    color: theme.palette.primary.main,
    label: 'Team Collaboration'
  }
};
  


  useEffect(() => {
    const handleNewNotification = (event) => {
      const data = event.detail;
      
      if (data.type === 'new_notification') {
        fetchNotifications(1, false);
        console.log('🎯 New real-time notification received:', data.notification.title);
        
        if (data.notification) {
          console.log(`🔔 ${data.notification.title}: ${data.notification.message}`);
        }
      }
    };

    window.addEventListener('new_notification', handleNewNotification);

    return () => {
      window.removeEventListener('new_notification', handleNewNotification);
    };
  }, []);

  // Debug function for notifications
  const debugNotification = (notification) => {
    console.log('Notification debug:', {
      id: notification._id,
      type: notification.type,
      title: notification.title,
      config: notificationTypes[notification.type],
      safeColor: getSafeColor(notificationTypes[notification.type], notification.type)
    });
  };

  const fetchNotifications = async (pageNum = 1, append = false) => {
    if (!UID) return;
    
    try {
      setLoading(true);
      const response = await fetch(
        `${url}/api/notifications/user/${UID}?page=${pageNum}&limit=20&type=${filterType === 'all' ? 'all' : filterType}`
      );
      const data = await response.json();
      
      if (data.data) {
        if (append) {
          setNotifications(prev => [...prev, ...data.data.notifications]);
        } else {
          setNotifications(data.data.notifications);
        }
        setHasMore(data.data.page < data.data.totalPages);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${url}/api/notifications/read/${notificationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: UID })
      });
      fetchNotifications();
      refreshUnreadCount();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${url}/api/notifications/read-all`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: UID })
      });
      fetchNotifications();
      refreshUnreadCount();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await fetch(`${url}/api/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: UID })
      });
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
      setAnchorEl(null);
      refreshUnreadCount();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

const handleNotificationClick = (notification) => {
  console.log('🚀 NOTIFICATION CLICK - FULL DEBUG:', {
    // Basic info
    id: notification._id,
    type: notification.type,
    title: notification.title,
    
    // Lead info
    leadId: notification.leadId,
    leadIdString: notification.leadId?._id,
    
    // Metadata info
    metadata: notification.metadata,
    collaborationId: notification.metadata?.collaborationId,
    metadataLeadId: notification.metadata?.leadId,
    
    // Type checks
    isTeamCollabAdded: notification.type === 'team_collaboration_added',
    isTeamCollabComment: notification.type === 'team_collaboration_comment',
    isLeadType: ['lead_assignment', 'quotation_shared', 'meeting_reminder'].includes(notification.type)
  });

  if (!notification.isRead) {
    markAsRead(notification._id);
  }
  
  setSelectedNotification(notification);

  // 🎯 ULTRA-SIMPLE LOGIC - Check type first
  if (notification.type === 'team_collaboration_added' || notification.type === 'team_collaboration_comment') {
    console.log('🎯 DETECTED: Team Collaboration Notification');
    
    const collaborationId = notification.metadata?.collaborationId;
    const leadId = notification.metadata?.leadId || notification.leadId?._id;
    
    console.log('🎯 NAVIGATING TO:', {
      path: `/team-collaboration/${collaborationId}`,
      query: leadId ? `?leadId=${leadId}` : '',
      fullPath: `/team-collaboration/${collaborationId}${leadId ? `?leadId=${leadId}` : ''}`
    });
    
    if (collaborationId) {
      navigate(`/team-collaboration/${collaborationId}${leadId ? `?leadId=${leadId}` : ''}`);
    } else {
      console.log('⚠️ No collaborationId found, going to list');
      navigate('/team-collaboration');
    }
    return;
  }
  
  // If it's a lead notification
  if (notification.leadId && ['lead_assignment', 'quotation_shared', 'meeting_reminder'].includes(notification.type)) {
    console.log('🎯 DETECTED: Lead Notification - Going to leads');
    navigate(`/leads?leadId=${notification.leadId._id}`);
    return;
  }
  
  console.log('📄 Showing detail dialog');
  setDetailDialogOpen(true);
};

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  useEffect(() => {
    fetchNotifications();
  }, [UID, filterType]);

  useEffect(() => {
    if (!window.AudioContext && !window.webkitAudioContext) {
      setAudioSupported(false);
    }
  }, []);

  const filteredNotifications = notifications.filter(notification => {
    if (tabValue === 1) return !notification.isRead;
    if (tabValue === 2) return notification.isRead;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const clearFilter = () => {
    setFilterType('all');
  };

  return (
    <Container maxWidth="lg" sx={{ 
      py: { xs: 2, md: 3 }, 
      mt: -5,
      bgcolor: 'background.default',
      minHeight: '100vh'
    }}>
      {/* Fixed Header Section */}
      <Box sx={{ 
        position: 'sticky', 
        top: 0, 
        zIndex: 10, 
        pb: 2,
        pt: 1,
        bgcolor: 'background.default'
      }}>
        {/* Dashboard Button and Controls */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 3
        }}>
          <Button
            variant="outlined"
            startIcon={<Dashboard />}
            onClick={() => navigate('/dashboard')}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              alignSelf: { xs: 'flex-start', md: 'center' },
              backgroundColor: "primary.main",
              color: mode === "light" ? "#fff" : "#0f172a",
              borderColor: "primary.main",
              '&:hover': {
                backgroundColor: mode === "dark" ? "#1e293b" : "#0a0e2c",
                borderColor: "primary.main",
              }
            }}
          >
            Back To Dashboard
          </Button>

          <Box sx={{ 
            display: 'flex', 
            gap: 1,
            alignSelf: { xs: 'flex-end', md: 'center' }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={soundEnabled}
                    onChange={(e) => setSoundEnabled(e.target.checked)}
                    disabled={!audioSupported}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {soundEnabled ? <VolumeUp fontSize="small" /> : <VolumeOff fontSize="small" />}
                  </Box>
                }
              />
            </Box>
            <IconButton
              onClick={(e) => setFilterAnchor(e.currentTarget)}
              sx={{ 
                border: '1px solid', 
                borderColor: 'divider',
                color: 'text.primary'
              }}
              size={isMobile ? "small" : "medium"}
            >
              <FilterList />
            </IconButton>
            <IconButton
              onClick={() => fetchNotifications()}
              sx={{ 
                border: '1px solid', 
                borderColor: 'divider',
                color: 'text.primary'
              }}
              size={isMobile ? "small" : "medium"}
            >
              <Refresh />
            </IconButton>
          </Box>
        </Box>

        {/* Filter Chip */}
        {filterType !== 'all' && (
          <Box sx={{ mb: 2 }}>
            <Chip
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {notificationTypes[filterType]?.icon}
                  {notificationTypes[filterType]?.label}
                </Box>
              }
              onDelete={clearFilter}
              deleteIcon={<Clear />}
              variant="outlined"
              sx={{
                backgroundColor: alpha(getSafeColor(notificationTypes[filterType], filterType), 0.15),
                borderColor: getSafeColor(notificationTypes[filterType], filterType),
                color: getSafeColor(notificationTypes[filterType], filterType),
                fontWeight: '500'
              }}
            />
          </Box>
        )}

        {/* Tabs with spacing */}
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          mb: 2
        }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 'auto',
                px: { xs: 2, md: 3 },
                py: 1,
                mx: { xs: 0.5, md: 1 },
                fontSize: { xs: '0.875rem', md: '1rem' }
              }
            }}
          >
            <Tab label="All" />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Unread
                  {unreadCount > 0 && (
                    <Box sx={{ 
                      bgcolor: "primary.main", 
                      color: 'white', 
                      borderRadius: '10px', 
                      px: 1, 
                      py: 0.25,
                      fontSize: '0.75rem',
                      minWidth: '20px',
                      textAlign: 'center'
                    }}>
                      {unreadCount}
                    </Box>
                  )}
                </Box>
              } 
            />
            <Tab label="Read" />
          </Tabs>
        </Box>
      </Box>

      {/* Scrollable Notifications List */}
      <Box sx={{ 
        height: 'calc(100vh - 200px)',
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: theme.palette.mode === 'dark' ? '#555' : '#ddd',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: theme.palette.mode === 'dark' ? '#777' : '#ccc',
        }
      }}>
        <Card sx={{ 
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 0 }}>
            {loading && page === 1 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: "primary.main" }} />
              </Box>
            ) : filteredNotifications.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <NotificationsIcon sx={{ 
                  fontSize: { xs: 48, md: 64 }, 
                  color: 'text.secondary', 
                  opacity: 0.3, 
                  mb: 2 
                }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No notifications
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {tabValue === 1 ? "You're all caught up!" : 
                  "You don't have any notifications yet."}
                </Typography>
              </Box>
            ) : (
              <List sx={{ py: 0 }}>
                {filteredNotifications.map((notification, index) => {
                  const config = getNotificationConfig(notification.type);
                  const safeColor = getSafeColor(config, notification.type);
                  
                  return (
                    <React.Fragment key={notification._id}>
                      <ListItem
                        sx={{
                          backgroundColor: notification.isRead ? 'transparent' : primaryLighter,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: notification.isRead ? 'action.hover' : primaryLight
                          },
                          px: { xs: 2, md: 3 },
                          py: { xs: 2, md: 2.5 },
                          position: 'relative'
                        }}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        {/* Unread indicator */}
                        {!notification.isRead && (
                          <Box
                            sx={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 3,
                              backgroundColor: "primary.main",
                              borderTopRightRadius: 4,
                              borderBottomRightRadius: 4
                            }}
                          />
                        )}

                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          width: '100%', 
                          gap: { xs: 1.5, md: 2 } 
                        }}>
                          {/* Icon */}
                          <Avatar
                            sx={{
                              bgcolor: alpha(safeColor, 0.15),
                              color: safeColor,
                              width: { xs: 40, md: 48 },
                              height: { xs: 40, md: 48 },
                              mt: 0.5
                            }}
                          >
                            {config.icon}
                          </Avatar>
                          
                          {/* Content */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'flex-start', 
                              justifyContent: 'space-between', 
                              mb: 1,
                              gap: 1
                            }}>
                              <Typography 
                                variant="subtitle1" 
                                fontWeight={notification.isRead ? "500" : "600"}
                                sx={{ 
                                  flex: 1,
                                  color: notification.isRead ? 'text.primary' : "primary.main",
                                  fontSize: { xs: '0.9rem', md: '1rem' }
                                }}
                              >
                                {notification.title}
                              </Typography>
                              
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAnchorEl(e.currentTarget);
                                  setSelectedNotification(notification);
                                }}
                                sx={{ color: 'text.secondary' }}
                              >
                                <MoreIcon />
                              </IconButton>
                            </Box>
                            
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ 
                                mb: 2, 
                                lineHeight: 1.5,
                                fontSize: { xs: '0.8rem', md: '0.875rem' }
                              }}
                            >
                              {notification.message}
                            </Typography>
                            
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              flexDirection: { xs: 'column', sm: 'row' },
                              gap: 1,
                              alignItems: { xs: 'flex-start', sm: 'center' }
                            }}>
                              <Chip 
                                label={config.label}
                                size="small"
                                sx={{
                                  backgroundColor: alpha(safeColor, 0.15),
                                  color: safeColor,
                                  fontWeight: '500',
                                  fontSize: '0.75rem'
                                }}
                              />
                              
                              <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                fontWeight="500"
                                sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                              >
                                {getTimeAgo(notification.createdAt)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </ListItem>
                      
                      {index < filteredNotifications.length - 1 && (
                        <Divider sx={{ mx: { xs: 2, md: 3 } }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </List>
            )}

            {/* Load More */}
            {hasMore && !loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <Button 
                  onClick={() => setPage(prev => prev + 1)} 
                  variant="outlined"
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    borderColor: "primary.main",
                    color: "primary.main",
                    '&:hover': {
                      borderColor: "primary.main",
                      backgroundColor: primaryLighter
                    }
                  }}
                >
                  Load More
                </Button>
              </Box>
            )}

            {loading && page > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} sx={{ color: "primary.main" }} />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Mark All Read FAB */}
      {unreadCount > 0 && (
        <Box sx={{ 
          position: 'fixed', 
          bottom: { xs: 16, md: 24 }, 
          right: { xs: 16, md: 24 },
          zIndex: 1000
        }}>
          <Button
            variant="contained"
            startIcon={<MarkEmailRead />}
            onClick={markAllAsRead}
            sx={{
              bgcolor: "primary.main",
              borderRadius: 3,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              fontSize: { xs: '0.8rem', md: '0.875rem' },
              color: '#ffffff',
              '&:hover': {
                bgcolor: mode === "dark" ? "#1e293b" : "#0a0e2c",
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.3)'
              }
            }}
          >
            Mark all as read
          </Button>
        </Box>
      )}

      {/* Notification Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            m: { xs: 2, md: 3 },
            bgcolor: 'background.paper'
          }
        }}
      >
        {selectedNotification && (
          <>
            <DialogTitle sx={{ 
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: alpha(getSafeColor(getNotificationConfig(selectedNotification.type), selectedNotification.type), 0.15),
                    color: getSafeColor(getNotificationConfig(selectedNotification.type), selectedNotification.type),
                    width: 48,
                    height: 48
                  }}
                >
                  {getNotificationConfig(selectedNotification.type).icon}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="600" color="text.primary">
                    {selectedNotification.title}
                  </Typography>
                  <Chip 
                    label={getNotificationConfig(selectedNotification.type).label}
                    size="small"
                    sx={{
                      backgroundColor: alpha(getSafeColor(getNotificationConfig(selectedNotification.type), selectedNotification.type), 0.15),
                      color: getSafeColor(getNotificationConfig(selectedNotification.type), selectedNotification.type),
                      fontWeight: '500',
                      fontSize: '0.7rem'
                    }}
                  />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ py: 3, bgcolor: 'background.paper' }}>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.6, color: 'text.primary' }}>
                {selectedNotification.message}
              </Typography>
              
              {selectedNotification.metadata && Object.keys(selectedNotification.metadata).length > 0 && (
                <Paper sx={{ 
                  p: 2, 
                  mt: 2, 
                  backgroundColor: mode === 'dark' ? 'grey.800' : 'grey.50', 
                  borderRadius: 2 
                }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="600" color="text.primary">
                    Additional Details
                  </Typography>
                  {Object.entries(selectedNotification.metadata).map(([key, value]) => (
                    <Box key={key} sx={{ display: 'flex', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: '500', minWidth: 120, color: 'text.primary' }}>
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {value?.toString()}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              )}
              
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">
                  Received: {new Date(selectedNotification.createdAt).toLocaleString()}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, gap: 1, bgcolor: 'background.paper' }}>
              <Button 
                onClick={() => setDetailDialogOpen(false)}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none',
                  color: 'text.secondary'
                }}
              >
                Close
              </Button>
            {selectedNotification && selectedNotification.leadId && (
  <Button 
    variant="contained"
    onClick={() => {
      console.log('🎯 Navigating to leads with highlight for:', selectedNotification.leadId._id);
      navigate(`/leads?leadId=${selectedNotification.leadId._id}`);
      setDetailDialogOpen(false);
    }}
    sx={{ 
      borderRadius: 2, 
      textTransform: "none",
      bgcolor: "primary.main",
      color: '#ffffff',
      '&:hover': { 
        bgcolor: mode === "dark" ? "#1e293b" : "#0a0e2c" 
      }
    }}
  >
    View Lead
  </Button>
)}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Notification Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: { 
            borderRadius: 2, 
            minWidth: 140,
            bgcolor: 'background.paper'
          }
        }}
      >
        {selectedNotification && !selectedNotification.isRead && (
          <MenuItem 
            onClick={() => {
              markAsRead(selectedNotification._id);
              setAnchorEl(null);
            }}
            sx={{ fontSize: '0.9rem', color: 'text.primary' }}
          >
            <MarkEmailRead sx={{ mr: 1, fontSize: 20 }} />
            Mark as Read
          </MenuItem>
        )}
        <MenuItem 
          onClick={() => {
            deleteNotification(selectedNotification._id);
          }}
          sx={{ fontSize: '0.9rem', color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
        PaperProps={{
          sx: { 
            borderRadius: 2, 
            minWidth: 200,
            bgcolor: 'background.paper'
          }
        }}
      >
        <MenuItem 
          selected={filterType === 'all'}
          onClick={() => {
            setFilterType('all');
            setFilterAnchor(null);
          }}
          sx={{ fontSize: '0.9rem', color: 'text.primary' }}
        >
          All Types
        </MenuItem>
        <Divider />
        {Object.entries(notificationTypes).map(([type, config]) => (
          <MenuItem
            key={type}
            selected={filterType === type}
            onClick={() => {
              setFilterType(type);
              setFilterAnchor(null);
            }}
            sx={{ fontSize: '0.9rem', color: 'text.primary' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: alpha(getSafeColor(config, type), 0.15),
                  color: getSafeColor(config, type),
                  width: 32,
                  height: 32
                }}
              >
                {config.icon}
              </Avatar>
              {config.label}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Container>
  );
};

export default NotificationScreen;